import { NgModule, Optional, SkipSelf } from '@angular/core';
import { RouterModule } from '@angular/router';

import { AlreadyLoggedInGuard, AuthGuard, throwIfAlreadyLoaded } from './core';
import { AnaliseMercadoGuard } from './core/guards/customer/analise-mercado.guard';
import { MatrizPicGuard } from './core/guards/customer/matriz-pic.guard';
import { PeriodicidadeCompraGuard } from './core/guards/environment/periodicidade-compra.guard';
import { CanActivateAIPage } from './domains/aya/guards/can-access-page.guard';
import { CentralConfiguracoesGuard } from './domains/core/settings/central-configuracoes.guard';
import { ForbiddenComponent } from './forbidden/forbidden.component';
import { CLIENTE_ANALISES_MERCADOS_PATH_ROUTE } from './home/cliente-analise-mercado/routes';
import { CanActivateDashboard, DashboardComponent } from './home/dashboard';
import { CROSS_SELL_KPIS_PATH_ROUTE } from './home/kpis/cross-sell/routes';
import { PRODUTIVIDADE_KPIS_PATH_ROUTE } from './home/kpis/produtividade/routes';
import { CLIENTES_PATH_ROUTE } from './home/pessoa/cliente/routes';
import { ShellComponent } from './shell/shell.component';
import { LoginComponent } from './login';
import { NotFoundComponent } from './not-found/not-found.component';

@NgModule({
  imports: [
    RouterModule.forRoot([
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [AlreadyLoggedInGuard]
      },
      {
        path: 'design-system',
        loadChildren: () =>
          import('app/design-system/design-system.module').then(
            (m) => m.DesignSystemModule
          )
      },
      {
        path: '',
        component: ShellComponent,
        canActivateChild: [AuthGuard],
        children: [
          {
            path: '',
            redirectTo: 'ceppem-ai',
            pathMatch: 'full'
          },
          {
            path: 'acesso-negado',
            component: ForbiddenComponent
          },
          {
            path: 'dashboard',
            component: DashboardComponent,
            canActivateChild: [CanActivateDashboard],
            children: [
              {
                path: '',
                loadChildren: () =>
                  import('app/home/dashboard/main/dashboard-main.module').then(
                    (m) => m.DashboardMainModule
                  )
              },
              {
                path: 'clientes',
                loadChildren: () =>
                  import('app/home/dashboard/cliente/dashboard-cliente.module').then(
                    (m) => m.DashboardClienteModule
                  )
              },
              {
                path: 'metas',
                loadChildren: () =>
                  import('app/home/dashboard/meta/dashboard-meta.module').then(
                    (m) => m.DashboardMetaModule
                  )
              },
              {
                path: 'faturamento-linhas',
                loadChildren: () =>
                  import('app/home/dashboard/meta-representante-linha/dashboard-meta-representante-linha.module').then(
                    (m) => m.DashboardMetaRepresentanteLinhaModule
                  )
              },
              {
                path: 'faturamento-grupos',
                loadChildren: () =>
                  import('app/home/dashboard/faturamento-grupo/dashboard-faturamento-grupo.module').then(
                    (m) => m.DashboardFaturamentoGrupoModule
                  )
              },
              {
                path: 'bonificacoes',
                loadChildren: () =>
                  import('app/home/dashboard/bonificacao/dashboard-bonificacao.module').then(
                    (m) => m.DashboardBonificacaoModule
                  )
              },
              {
                path: 'financeiro',
                loadChildren: () =>
                  import('app/home/dashboard/financeiro/dashboard-financeiro.module').then(
                    (m) => m.DashboardFinanceiroModule
                  )
              },
              {
                path: 'minha-regiao',
                loadChildren: () =>
                  import('app/home/dashboard/minha-regiao/dashboard-minha-regiao.module').then(
                    (m) => m.DashboardMinhaRegiaoModule
                  )
              },
              {
                path: 'tv',
                loadChildren: () =>
                  import('app/home/dashboard/tv/dashboard-tv.module').then(
                    (m) => m.DashboardTvModule
                  )
              }
            ]
          },
          {
            path: 'cadastros',
            children: [
              {
                path: 'classificacoes-faturamento',
                loadChildren: () =>
                  import('app/home/classificacao-faturamento/classificacao-faturamento.module').then(
                    (m) => m.ClassificacaoFaturamentoModule
                  )
              },
              {
                path: 'concorrentes',
                redirectTo: '/configuracoes/concorrentes',
                pathMatch: 'full'
              },
              {
                path: 'concorrentes/novo',
                redirectTo: '/configuracoes/concorrentes/novo'
              },
              {
                path: 'concorrentes/:id',
                redirectTo: '/configuracoes/concorrentes/:id'
              },
              {
                path: 'condicoes-pagamentos',
                loadChildren: () =>
                  import('app/home/condicao-pagamento/condicao-pagamento.module').then(
                    (m) => m.CondicaoPagamentoModule
                  )
              },
              {
                path: 'formas-pagamentos',
                loadChildren: () =>
                  import('app/home/forma-pagamento/forma-pagamento.module').then(
                    (m) => m.FormaPagamentoModule
                  )
              },
              {
                path: 'mercados',
                loadChildren: () =>
                  import('app/home/mercado/mercado.module').then(
                    (m) => m.MercadoModule
                  )
              },
              {
                path: 'regioes',
                loadChildren: () =>
                  import('app/home/regiao/regiao.module').then(
                    (m) => m.RegiaoModule
                  )
              },
              {
                path: 'representantes',
                loadChildren: () =>
                  import('app/home/pessoa/representante/representante.module').then(
                    (m) => m.RepresentanteModule
                  )
              },
              {
                path: 'tabelas-precos',
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    loadChildren: () =>
                      import('app/domains/preparation/tabelas-precos/tabelas-precos.module').then(
                        (m) => m.TabelasPrecosModule
                      )
                  },
                  {
                    path: '',
                    loadChildren: () =>
                      import('app/home/tabela-preco/tabela-preco.module').then(
                        (m) => m.TabelaPrecoModule
                      )
                  }
                ]
              },
              {
                path: 'transportadoras',
                loadChildren: () =>
                  import('app/home/pessoa/transportadora/transportadora.module').then(
                    (m) => m.TransportadoraModule
                  )
              }
            ]
          },
          {
            path: 'configuracoes',
            children: [
              {
                path: 'areas',
                loadChildren: () =>
                  import('app/home/areas/area.module').then((m) => m.AreaModule)
              },
              {
                path: 'cnaes',
                loadChildren: () =>
                  import('app/home/cnaes/cnae.module').then((m) => m.CNAEModule)
              },
              {
                path: 'campanhas/recursos-campanhas',
                loadChildren: () =>
                  import('app/home/campanhas/recursos/recurso-campanha.module').then(
                    (m) => m.RecursoCampanhaModule
                  )
              },
              {
                path: 'campanhas/pacotes-campanhas',
                loadChildren: () =>
                  import('app/home/campanhas/pacotes/pacote-campanha.module').then(
                    (m) => m.PacoteCampanhaModule
                  )
              },
              {
                path: 'feriados',
                loadChildren: () =>
                  import('app/domains/environment/feriado/feriado.module').then(
                    (m) => m.FeriadoModule
                  )
              },
              {
                path: 'grades-horarias',
                loadChildren: () =>
                  import('app/home/grade-horaria/grade-horaria.module').then(
                    (m) => m.GradeHorariaModule
                  )
              },
              {
                path: 'motivos',
                loadChildren: () =>
                  import('app/home/motivo-perda-venda/motivo-perda-venda.module').then(
                    (m) => m.MotivoPerdaVendaModule
                  )
              },
              {
                path: 'periodicidades-compras',
                loadChildren: () =>
                  import('app/domains/environment/periodicidade-compra/periodicidade-compra.module').then(
                    (m) => m.PeriodicidadeCompraModule
                  ),
                canActivate: [PeriodicidadeCompraGuard]
              },
              {
                path: 'tipos-arquivos',
                loadChildren: () =>
                  import('app/home/arquivos/tipos/tipo-arquivo.module').then(
                    (m) => m.TipoArquivoModule
                  )
              },
              {
                path: 'integracoes-logs',
                loadChildren: () =>
                  import('app/home/configuracoes/integration-log/integration-log.module').then(
                    (m) => m.IntegrationLogModule
                  )
              },
              {
                path: 'usuarios',
                loadChildren: () =>
                  import('app/home/usuario/usuario.module').then(
                    (m) => m.UsuarioModule
                  )
              },
              {
                path: 'usuario-perfil',
                loadChildren: () =>
                  import('app/home/usuario-perfil/usuario-perfil.module').then(
                    (m) => m.UsuarioPerfilModule
                  )
              },
              {
                path: 'formularios-cotacoes',
                loadChildren: () =>
                  import('app/home/cotacao-item-formulario/cotacao-item-formulario.module').then(
                    (m) => m.CotacaoItemFormularioModule
                  )
              },
              {
                path: 'locais-depositos',
                loadChildren: () =>
                  import('app/home/local-deposito/local-deposito.module').then(
                    (m) => m.LocalDepositoModule
                  )
              },
              {
                path: 'locais-faturamentos',
                loadChildren: () =>
                  import('app/home/local-faturamento/local-faturamento.module').then(
                    (m) => m.LocalFaturamentoModule
                  )
              },
              {
                path: 'cidades',
                loadChildren: () =>
                  import('app/home/cidade/cidade.module').then(
                    (m) => m.CidadeModule
                  )
              },
              {
                path: 'periodos',
                loadChildren: () =>
                  import('app/home/periodo/periodo.module').then(
                    (m) => m.PeriodoModule
                  )
              },
              {
                path: 'tipos-segmentos',
                loadChildren: () =>
                  import('app/home/tipo-classificacao/tipo-classificacao.module').then(
                    (m) => m.TipoClassificacaoModule
                  )
              },
              {
                path: 'segmentos',
                loadChildren: () =>
                  import('app/domains/environment/segmentos/segmentos.module').then(
                    (m) => m.SegmentosModule
                  )
              },
              {
                path: 'publicos-alvo',
                loadChildren: () =>
                  import(
                    'app/domains/environment/publicos-alvo/publico-alvo.module'
                  ).then((m) => m.PublicoAlvoModule)
              },
              {
                path: 'tags',
                loadChildren: () =>
                  import('app/domains/environment/tags/tags.module').then(
                    (m) => m.TagsModule
                  )
              },
              {
                path: 'condicoes-pagamentos',
                loadChildren: () =>
                  import(
                    'app/domains/environment/condicoes-pagamentos/condicoes-pagamentos.module'
                  ).then((m) => m.CondicoesPagamentosModule)
              },
              {
                path: 'concorrentes',
                loadChildren: () =>
                  import('app/domains/environment/concorrentes/concorrentes.module').then(
                    (m) => m.ConcorrentesModule
                  )
              },
              {
                path: 'campos-customizados',
                loadChildren: () =>
                  import('app/domains/core/fields/fields.module').then(
                    (m) => m.FieldsModule
                  )
              },
              {
                path: 'formularios-customizados',
                loadChildren: () =>
                  import('app/domains/core/forms/forms.module').then(
                    (m) => m.CustomFormsModule
                  )
              },
              {
                path: 'grupos-segmentos',
                loadChildren: () =>
                  import('app/home/grupo-classificacao/grupo-classificacao.module').then(
                    (m) => m.GrupoClassificacaoModule
                  )
              },
              {
                path: 'biotipos',
                loadChildren: () =>
                  import('app/domains/product/biotipos/biotipos.module').then(
                    (m) => m.BiotiposModule
                  )
              },
              {
                path: 'dnas',
                loadChildren: () =>
                  import('app/domains/product/dnas/dnas.module').then(
                    (m) => m.DnasModule
                  )
              },
              {
                path: '',
                canActivate: [CentralConfiguracoesGuard],
                loadChildren: () =>
                  import('app/domains/core/settings/settings.module').then(
                    (m) => m.SettingsModule
                  )
              }
            ]
          },
          {
            path: 'atividades',
            loadChildren: () =>
              import('app/home/atividade/atividade.module').then(
                (m) => m.AtividadeModule
              )
          },
          {
            path: 'analise-sellin',
            loadChildren: () =>
              import('app/home/analise-sellin/analise-sellin.module').then(
                (m) => m.AnaliseSellInModule
              )
          },
          {
            path: 'analises-market-share',
            loadChildren: () =>
              import('app/domains/preparation/analise-market-share/analise-market-share.module').then(
                (m) => m.AnaliseMarketShareModule
              )
          },

          {
            path: 'prospeccao-outbound',
            loadChildren: () =>
              import('app/home/prospeccao-outbound/prospeccao-outbound.module').then(
                (m) => m.ProspeccaoOutboundModule
              )
          },
          {
            path: 'leads',
            loadChildren: () =>
              import('app/home/crm/lead/lead.module').then((m) => m.LeadModule)
          },
          {
            path: CLIENTES_PATH_ROUTE,
            children: [
              {
                path: 'cadastro',
                loadChildren: () =>
                  import('app/home/pessoa/cliente/cliente.module').then(
                    (m) => m.ClienteModule
                  )
              },
              {
                path: CLIENTE_ANALISES_MERCADOS_PATH_ROUTE + '-old',
                loadChildren: () =>
                  import('app/home/cliente-analise-mercado/cliente-analise-mercado.module').then(
                    (m) => m.ClienteAnaliseMercadoModule
                  )
              },
              {
                // OLD
                path: 'analise-mercado-v1',
                loadChildren: () =>
                  import('app/home/analise-mercado/analise-mercado.module').then(
                    (m) => m.AnaliseMercadoModule
                  ),
                canActivate: [AnaliseMercadoGuard]
              },
              {
                path: 'carteira-prospects',
                loadChildren: () =>
                  import('app/home/cliente-carteira/carteiras/prospect-carteira/prospect-carteira.module').then(
                    (m) => m.ProspectCarteiraModule
                  )
              },
              {
                path: 'carteira-clientes',
                loadChildren: () =>
                  import('app/home/cliente-carteira/carteiras/cliente-carteira/cliente-carteira.module').then(
                    (m) => m.ClienteCarteiraModule
                  )
              },
              {
                path: 'matriz-pic',
                loadChildren: () =>
                  import('app/domains/customer/matriz-pic/matriz-pic.module').then(
                    (m) => m.MatrizPicModule
                  ),
                canActivate: [MatrizPicGuard]
              },
              {
                path: 'rap-3d',
                loadChildren: () =>
                  import('app/domains/customer/analise-mercado/analise-mercado.module').then(
                    (m) => m.AnaliseMercadoModule
                  ),
                canActivate: [AnaliseMercadoGuard]
              },
              {
                path: 'internet',
                loadChildren: () =>
                  import('app/home/pessoa/cliente/internet/cliente-internet.module').then(
                    (m) => m.ClienteInternetModule
                  )
              },
              {
                path: 'tipos',
                loadChildren: () =>
                  import('app/home/cliente-tipo/cliente-tipo.module').then(
                    (m) => m.ClienteTipoModule
                  )
              },
              {
                path: 'grupos',
                loadChildren: () =>
                  import('app/home/cliente-grupo/cliente-grupo.module').then(
                    (m) => m.ClienteGrupoModule
                  )
              },
              {
                path: 'grupos-economicos',
                loadChildren: () =>
                  import('app/home/cliente-grupo-economico/cliente-grupo-economico.module').then(
                    (m) => m.ClienteGrupoEconomicoModule
                  )
              }
            ]
          },
          {
            path: 'itens',
            loadChildren: () =>
              import('app/domains/product/itens/itens.module').then(
                (m) => m.ItensModule
              )
          },
          {
            path: 'classificacoes-plm',
            loadChildren: () =>
              import('app/domains/product/classificacoes-plm/classificacoes-plm.module').then(
                (m) => m.ClassificacoesPLMModule
              )
          },
          {
            path: 'plm',
            loadChildren: () =>
              import('app/domains/product/plm/plm.module').then((m) => m.PlmModule)
          },
          {
            path: 'itens-unidades',
            loadChildren: () =>
              import('app/home/item-unidade/item-unidade.module').then(
                (m) => m.ItemUnidadeModule
              )
          },
          {
            path: 'unidades-negocio',
            loadChildren: () =>
              import('app/home/unidade-negocio/unidade-negocio.module').then(
                (m) => m.UnidadeNegocioModule
              )
          },

          {
            path: 'cotacoes',
            loadChildren: () =>
              import('app/home/cotacao/cotacao.module').then(
                (m) => m.CotacaoModule
              )
          },
          {
            path: 'negocios',
            loadChildren: () =>
              import('app/home/crm/negocio/negocio.module').then(
                (m) => m.NegocioModule
              )
          },

          // CAMPANHAS
          {
            path: 'campanhas',
            loadChildren: () =>
              import('app/home/campanhas/campanhas/campanha.module').then(
                (m) => m.CampanhaModule
              )
          },

          {
            path: 'pedidos',
            loadChildren: () =>
              import('app/home/pedido/pedido.module').then(
                (m) => m.PedidoModule
              )
          },
          {
            path: 'expedicao',
            loadChildren: () =>
              import('app/home/expedicao/expedicao.module').then(
                (m) => m.ExpedicaoModule
              )
          },
          {
            path: 'coleta',
            loadChildren: () =>
              import('app/home/coleta/coleta.module').then(
                (m) => m.ColetaModule
              )
          },
          {
            path: 'notas-fiscais',
            loadChildren: () =>
              import('app/domains/execution/notas-fiscais/notas-fiscais.module').then(
                (m) => m.NotasFiscaisModule
              )
          },
          {
            path: 'metas',
            loadChildren: () =>
              import('app/home/meta/meta.module').then((m) => m.MetaModule)
          },
          {
            path: 'metas-representantes',
            loadChildren: () =>
              import('app/home/meta-representante/meta-representante.module').then(
                (m) => m.MetaRepresentanteModule
              )
          },
          {
            path: 'titulos',
            loadChildren: () =>
              import('app/home/titulo-receber/titulo-receber.module').then(
                (m) => m.TituloReceberModule
              )
          },
          {
            path: 'comissoes',
            loadChildren: () =>
              import('app/home/comissao/comissao.module').then(
                (m) => m.ComissaoModule
              )
          },
          {
            path: 'carteira-pedidos',
            loadChildren: () =>
              import('app/home/carteira-pedido/carteira-pedido.module').then(
                (m) => m.CarteiraPedidoModule
              )
          },
          {
            path: 'kpis',
            children: [
              {
                path: CROSS_SELL_KPIS_PATH_ROUTE,
                loadChildren: () =>
                  import('app/home/kpis/cross-sell/kpi-cross-sell.module').then(
                    (m) => m.KPICrossSellModule
                  )
              },
              {
                path: PRODUTIVIDADE_KPIS_PATH_ROUTE,
                loadChildren: () =>
                  import('app/home/kpis/produtividade/produtividade.module').then(
                    (m) => m.ProdutividadeModule
                  )
              }
            ]
          },
          {
            path: 'consultas',
            children: [
              {
                path: 'adimplencia',
                loadChildren: () =>
                  import('app/home/titulo-receber/adimplencia/consulta/titulo-receber-adimplencia-consulta.module').then(
                    (m) => m.TituloReceberAdimplenciaConsultaModule
                  )
              },
              {
                path: 'analise-financeira',
                loadChildren: () =>
                  import('app/home/consultas/analise-financeira/analise-financeira.module').then(
                    (m) => m.AnaliseFinanceiraModule
                  )
              },
              {
                path: 'analise-representante',
                loadChildren: () =>
                  import('app/home/consultas/analise-representante/analise-representante.module').then(
                    (m) => m.AnaliseRepresentanteModule
                  )
              },
              {
                path: 'bonificacoes',
                loadChildren: () =>
                  import('app/home/consultas/bonificacao/bonificacao.module').then(
                    (m) => m.BonificacaoModule
                  )
              },
              {
                path: 'bonificacoes-clientes',
                loadChildren: () =>
                  import('app/home/consultas/bonificacao-cliente/bonificacao-cliente.module').then(
                    (m) => m.BonificacaoClienteModule
                  )
              },
              {
                path: 'cotacoes-status',
                loadChildren: () =>
                  import('app/home/consultas/cotacao-status/cotacao-status.module').then(
                    (m) => m.CotacaoStatusModule
                  )
              },
              {
                path: 'cotacoes-perdidas',
                loadChildren: () =>
                  import('app/home/consultas/cotacao-perdida/cotacao-perdida.module').then(
                    (m) => m.CotacaoPerdidaModule
                  )
              },
              {
                path: 'cotacoes-perdidas-itens',
                loadChildren: () =>
                  import('app/home/consultas/cotacao-perdida-item/cotacao-perdida-item.module').then(
                    (m) => m.CotacaoPerdidaItemModule
                  )
              },
              {
                path: 'curva-abc-vendas',
                loadChildren: () =>
                  import('app/home/consultas/curva-abc-venda/curva-abc-venda.module').then(
                    (m) => m.CurvaAbcVendaModule
                  )
              },
              {
                path: 'cotacoes-prazos-entregas',
                loadChildren: () =>
                  import('app/home/consultas/cotacao-prazo-entrega/cotacao-prazo-entrega.module').then(
                    (m) => m.CotacaoPrazoEntregaModule
                  )
              },
              {
                path: 'efetividade-vendas',
                loadChildren: () =>
                  import('app/home/consultas/efetividade-venda/efetividade-venda.module').then(
                    (m) => m.EfetividadeVendaModule
                  )
              },
              {
                path: 'metas',
                loadChildren: () =>
                  import('app/home/meta/consulta/meta-consulta.module').then(
                    (m) => m.MetaConsultaModule
                  )
              },
              {
                path: 'metas-faturamento',
                loadChildren: () =>
                  import('app/home/consultas/meta-faturamento/meta-faturamento.module').then(
                    (m) => m.MetaFaturamentoModule
                  )
              },
              {
                path: 'minha-regiao',
                loadChildren: () =>
                  import('app/home/consultas/minha-regiao/minha-regiao.module').then(
                    (m) => m.MinhaRegiaoModule
                  )
              },
              {
                path: 'pedidos-geps',
                loadChildren: () =>
                  import('app/home/consultas/pedido-gep/pedido-gep.module').then(
                    (m) => m.PedidoGepModule
                  )
              },
              {
                path: 'pedidos-status',
                loadChildren: () =>
                  import('app/home/consultas/pedido-status/pedido-status.module').then(
                    (m) => m.PedidoStatusModule
                  )
              },
              {
                path: 'pedido-resultado-liquido',
                loadChildren: () =>
                  import('app/home/consultas/pedido-resultado-liquido/pedido-resultado-liquido.module').then(
                    (m) => m.PedidoResultadoLiquidoModule
                  )
              },
              {
                path: 'rafitec',
                children: [
                  {
                    path: 'metas',
                    loadChildren: () =>
                      import('app/home/rafitec/consultas/meta/rafitec-meta.module').then(
                        (m) => m.RafitecMetaModule
                      )
                  }
                ]
              }
            ]
          },
          {
            path: 'relatorios',
            loadChildren: () =>
              import('app/domains/relatorios/relatorios.module').then(
                (m) => m.RelatoriosModule
              )
          },
          {
            path: 'ceppem-ai',
            canActivate: [CanActivateAIPage],
            loadChildren: () =>
              import('app/domains/aya/aya.module').then(
                (m) => m.AyaModule
              )
          },
          { path: '**', component: NotFoundComponent }
        ]
      }
    ])
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {
  constructor(@Optional() @SkipSelf() parentModule: AppRoutingModule) {
    throwIfAlreadyLoaded(parentModule, 'AppRoutingModule');
  }
}
